import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFreemium } from '../context/FreemiumContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Star, GitCompare, ArrowRight, X, CreditCard, Loader2, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const WA_NUM = '56998759833';
const wa = (msg) => `https://wa.me/${WA_NUM}?text=${encodeURIComponent(msg)}`;

const LIMIT_TITLES = {
    borradores: 'Alcanzaste el límite de 5 borradores nuevos',
    analisis_ia: 'Alcanzaste el límite de 5 análisis IA',
    diagnosticos: 'Alcanzaste el límite de 5 diagnósticos',
    stats_vistas: 'Alcanzaste el límite de 5 visualizaciones de estadísticas',
    sin_creditos_ia: 'Has utilizado tus créditos IA gratuitos',
    sin_mejoras_ia: 'Has utilizado tus 3 mejoras gratuitas',
};

export default function FreemiumLimitModal() {
    const navigate = useNavigate();
    const { modal, closeModal } = useFreemium();
    const [mpCfg, setMpCfg] = React.useState(null);
    const [payingPlan, setPayingPlan] = React.useState(null);

    React.useEffect(() => {
        if (!modal.open || mpCfg) return;
        api.get('/mp/config').then((r) => setMpCfg(r.data)).catch(() => {});
    }, [modal.open, mpCfg]);

    const iniciarPagoMP = async (plan) => {
        setPayingPlan(plan);
        try {
            const r = await api.post('/mp/preferences', { plan });
            const url = r.data?.init_point || r.data?.sandbox_init_point;
            if (!url) throw new Error('Preferencia sin init_point');
            window.location.assign(url);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail?.mensaje || 'No pudimos iniciar el pago con Mercado Pago.');
            setPayingPlan(null);
        }
    };

    if (!modal.open) return null;
    const payload = modal.payload || {};
    const isLegacy = payload.code === 'trial_gratuito_finalizado';
    const isSinCreditos = payload.code === 'sin_creditos_ia';
    const isSinMejoras = payload.code === 'sin_mejoras_ia';
    const limitType = payload.limit_type;

    const title = isLegacy
        ? 'El período gratuito de exploración finalizó'
        : isSinMejoras
            ? (payload.titulo || LIMIT_TITLES.sin_mejoras_ia)
            : isSinCreditos
                ? (payload.titulo || LIMIT_TITLES.sin_creditos_ia)
                : (LIMIT_TITLES[limitType] || 'Has utilizado todos los beneficios del modo Freemium');

    const bodyMsg = isLegacy
        ? 'Todo tu trabajo permanece guardado y disponible en modo lectura. Para crear nuevos borradores, ejecutar análisis IA o generar diagnósticos, activa el Plan Plataforma.'
        : isSinMejoras
            ? (payload.mensaje || 'Tu trabajo permanece guardado y puedes continuar editándolo. Si quieres seguir generando nuevas versiones Competente y Destacada para tus tareas, activa el Plan Plataforma.')
            : (payload.mensaje || 'Tu trabajo permanece guardado. Activa el Plan Plataforma para continuar creando nuevos borradores, obtener nuevos análisis con IA y seguir desarrollando tu Portafolio Docente 2026.');

    const goActivar = () => { closeModal(); navigate('/activar-plan'); };
    const goRevisar = () => closeModal();

    return (
        <Dialog open={modal.open} onOpenChange={(v) => { if (!v) closeModal(); }}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="freemium-limit-modal">
                {/* Header */}
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 px-6 py-6 sm:px-8 sm:py-7 text-white">
                    <button
                        onClick={closeModal}
                        className="absolute right-4 top-4 rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition"
                        aria-label="Cerrar"
                        data-testid="freemium-modal-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/20 border border-amber-300/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-200">
                        <Star className="h-3 w-3 fill-current" />
                        Activar Plan
                    </div>
                    <DialogTitle className="mt-3 font-heading text-xl sm:text-2xl font-bold">
                        {title}
                    </DialogTitle>
                    <DialogDescription className="mt-2 text-sm sm:text-base text-slate-200 leading-relaxed">
                        {bodyMsg}
                    </DialogDescription>
                </div>

                {/* Cuerpo con plan único */}
                <div className="px-6 py-6 sm:px-8 sm:py-7 bg-white">
                    <div className="max-w-sm mx-auto">
                        {/* Plataforma — único plan comercial */}
                        <div className="rounded-xl border-2 border-sky-200 bg-white p-4 flex flex-col shadow-sm">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">Plan Plataforma</div>
                            <div className="mt-1 font-mono text-2xl font-extrabold text-slate-900">$49.990</div>
                            <p className="mt-1 text-xs text-slate-600 leading-snug flex-1">
                                Acceso completo con IA especializada, análisis ilimitados y rúbricas oficiales CPEIP 2026. Pago único.
                            </p>
                            <a
                                href={wa('Hola. Deseo contratar el Plan Plataforma Portafolio Digital Docente.')}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="modal-plataforma-btn"
                                className={mpCfg?.configured ? "hidden" : "block"}
                            >
                                <Button className="w-full bg-slate-900 hover:bg-slate-800 gap-2 mt-3" size="sm">
                                    <MessageCircle className="h-3 w-3" />
                                    Coordinar por WhatsApp
                                </Button>
                            </a>
                            {mpCfg?.configured && (
                                <Button
                                    onClick={() => iniciarPagoMP('plataforma')}
                                    disabled={payingPlan === 'plataforma'}
                                    className="w-full bg-slate-900 hover:bg-slate-800 gap-2 mt-3"
                                    size="sm"
                                    data-testid="modal-mp-plataforma-btn"
                                >
                                    {payingPlan === 'plataforma' ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <CreditCard className="h-3 w-3" />
                                    )}
                                    Pagar con Mercado Pago
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Botones secundarios */}
                    <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={goActivar}
                            className="gap-2"
                            data-testid="modal-comparar-btn"
                        >
                            <GitCompare className="h-4 w-4" />
                            Ver Plan Plataforma en detalle
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={goRevisar}
                            className="text-slate-600"
                            data-testid="modal-seguir-revisando-btn"
                        >
                            Seguir revisando mi trabajo
                        </Button>
                    </div>

                    {/* Nota inferior */}
                    <p className="mt-4 pt-4 border-t border-slate-100 text-[11px] text-slate-500 text-center">
                        Todo tu trabajo permanece guardado. El pago solamente desbloquea nuevas acciones.
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
